import React from 'react';
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {Company, CompanyBooking, CompanyStatus} from '../../../state/types/Company';
import {calculateBGScore, getHighestVotes} from '../../../util/bgScore';

interface VersionHistoryDialogProps {
    open: boolean;
    companyId?: string;
    companyStatusMap: Record<string, CompanyStatus>;
    companyList: Company[];
    activeVersion: number;
    onClose: () => void;
}

const formatPercent = (score?: number, achievable?: number): string => {
    if (!score || !achievable) return '-';
    return `${(score / achievable * 100).toFixed(1)}%`;
};

const formatScore = (score?: number, achievable?: number): string => {
    if (!score || !achievable) return '-';
    return `${score} / ${achievable}`;
};

const VersionHistoryDialog = (props: VersionHistoryDialogProps) => {
    const company = props.companyList.find(c => c.id === props.companyId);
    const companyStatus = props.companyId ? props.companyStatusMap[props.companyId] : undefined;

    if (!company || !companyStatus) {
        return (
            <Dialog open={props.open} onClose={props.onClose} maxWidth={'md'} fullWidth={true}>
                <DialogTitle>Versionshistorie</DialogTitle>
                <DialogContent>
                    <Typography>Keine Daten verfügbar.</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    // Collect only versions that have actual data (bookings, tests, or voting)
    const versions: number[] = [];
    for (let v = 1; v <= props.activeVersion; v++) {
        const booking = company.bookings?.[v];
        const hasBooking = booking?.qualityCheck || booking?.marketing;
        const hasStatus = companyStatus.selfAssessment[v] ||
            companyStatus.websiteCheck[v] ||
            companyStatus.mysteryTest[v];
        const hasVoting = !!company.voting?.[v]?.votes;
        if (hasBooking || hasStatus || hasVoting) {
            versions.push(v);
        }
    }

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth={'lg'} fullWidth={true}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Versionshistorie: {company.restaurantName}</span>
                <IconButton onClick={props.onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
                {/* Booking History */}
                <Typography variant={'h6'} sx={{ mb: 1 }}>Buchungen</Typography>
                <TableContainer sx={{ mb: 3 }}>
                    <Table size={'small'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Version</TableCell>
                                <TableCell>Qualitätscheck</TableCell>
                                <TableCell>Lieblingslokal</TableCell>
                                <TableCell>Buchungsdatum</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {versions.map(v => {
                                const booking: CompanyBooking | undefined = company.bookings?.[v];
                                return (
                                    <TableRow key={v}>
                                        <TableCell>
                                            <Chip
                                                label={`v${v}`}
                                                size={'small'}
                                                color={v === props.activeVersion ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {booking?.qualityCheck
                                                ? <CheckCircleIcon sx={{ color: 'green', fontSize: 18 }} />
                                                : <CancelIcon sx={{ color: 'grey', fontSize: 18 }} />
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {booking?.marketing
                                                ? <CheckCircleIcon sx={{ color: 'green', fontSize: 18 }} />
                                                : <CancelIcon sx={{ color: 'grey', fontSize: 18 }} />
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {booking?.qualityCheckDate || booking?.marketingDate || '-'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Score Comparison */}
                <Typography variant={'h6'} sx={{ mb: 1 }}>Testergebnisse im Vergleich</Typography>
                <TableContainer sx={{ mb: 3 }}>
                    <Table size={'small'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Version</TableCell>
                                <TableCell>Selbstauskunft</TableCell>
                                <TableCell>Website-Check</TableCell>
                                <TableCell>Mystery Test</TableCell>
                                <TableCell>BG-Score</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {versions.map(v => {
                                const sa = companyStatus.selfAssessment[v];
                                const wc = companyStatus.websiteCheck[v];
                                const mt = companyStatus.mysteryTest[v];
                                const hvForVersion = getHighestVotes(props.companyList, v);
                                const bgs = calculateBGScore(company, hvForVersion, v);

                                return (
                                    <TableRow key={v}>
                                        <TableCell>
                                            <Chip
                                                label={`v${v}`}
                                                size={'small'}
                                                color={v === props.activeVersion ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant={'body2'}>
                                                    {formatPercent(sa?.score, sa?.achievableScore)}
                                                </Typography>
                                                <Typography variant={'caption'} color={'text.secondary'}>
                                                    {formatScore(sa?.score, sa?.achievableScore)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant={'body2'}>
                                                    {formatPercent(wc?.score, wc?.achievableScore)}
                                                </Typography>
                                                <Typography variant={'caption'} color={'text.secondary'}>
                                                    {formatScore(wc?.score, wc?.achievableScore)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant={'body2'}>
                                                    {formatPercent(mt?.score, mt?.achievableScore)}
                                                </Typography>
                                                <Typography variant={'caption'} color={'text.secondary'}>
                                                    {formatScore(mt?.score, mt?.achievableScore)}
                                                    {mt?.attempt && mt.attempt > 1
                                                        ? ` (${mt.attempt} Versuche)` : ''}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant={'body2'} fontWeight={'bold'}>
                                                {bgs > 0 ? bgs : '-'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Reports */}
                <Typography variant={'h6'} sx={{ mb: 1 }}>Reports</Typography>
                <TableContainer sx={{ mb: 3 }}>
                    <Table size={'small'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Version</TableCell>
                                <TableCell>Admin-Report</TableCell>
                                <TableCell>Unternehmensreport</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {versions.map(v => {
                                const mt = companyStatus.mysteryTest[v];
                                if (!mt?.submitted || !mt?.mysteryTestId) return null;
                                return (
                                    <TableRow key={v}>
                                        <TableCell>
                                            <Chip
                                                label={`v${v}`}
                                                size={'small'}
                                                color={v === props.activeVersion ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant={'body2'}
                                                component={'a'}
                                                href={`/api/reports/mystery-test/${mt.mysteryTestId}`}
                                                target={'_blank'}
                                                sx={{ cursor: 'pointer', color: 'primary.main' }}
                                            >
                                                Admin-Report v{v}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant={'body2'}
                                                component={'a'}
                                                href={`/api/reports/mystery-test/${mt.mysteryTestId}_company`}
                                                target={'_blank'}
                                                sx={{ cursor: 'pointer', color: 'primary.main' }}
                                            >
                                                Unternehmensreport v{v}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Contract Archive */}
                <Typography variant={'h6'} sx={{ mb: 1 }}>Verträge</Typography>
                <TableContainer>
                    <Table size={'small'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Version</TableCell>
                                <TableCell>Buchungsvertrag</TableCell>
                                <TableCell>Gutschein</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Legacy (unversioned) contract */}
                            <TableRow>
                                <TableCell>
                                    <Chip label={'Ohne Versionsnummer'} size={'small'} variant={'outlined'} />
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant={'body2'}
                                        component={'a'}
                                        href={`/api/contract/${company.id}`}
                                        target={'_blank'}
                                        sx={{ cursor: 'pointer', color: 'text.secondary' }}
                                    >
                                        Vertrag (Original)
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant={'body2'}
                                        component={'a'}
                                        href={`/api/contract/${company.id}_voucher`}
                                        target={'_blank'}
                                        sx={{ cursor: 'pointer', color: 'text.secondary' }}
                                    >
                                        Gutschein (Original)
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            {/* Versioned contracts */}
                            {versions.map(v => (
                                <TableRow key={v}>
                                    <TableCell>
                                        <Chip
                                            label={`v${v}`}
                                            size={'small'}
                                            color={v === props.activeVersion ? 'primary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant={'body2'}
                                            component={'a'}
                                            href={`/api/contract/${company.id}/v/${v}`}
                                            target={'_blank'}
                                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                                        >
                                            Vertrag v{v}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant={'body2'}
                                            component={'a'}
                                            href={`/api/contract/${company.id}_voucher/v/${v}`}
                                            target={'_blank'}
                                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                                        >
                                            Gutschein v{v}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
};

export default VersionHistoryDialog;
